import { Badge } from '@/components/ui/badge'
import { cn } from '@/lib/utils'
import {
  formatDuration,
  formatPhone,
} from '@/features/calls/utils/callDetailFormat'
import type { TransferDisplayInfo } from '@/features/calls/utils/transferInfo'

interface BadgeConfig {
  text: string
  bg: string
  color: string
  /**
   * Only the fallback "raw, unrecognized status" badge needs CSS
   * `capitalize` — every known status below already ships display-ready
   * text, and `capitalize` mis-renders hyphenated values (e.g. "no-answer"
   * → "No-answer" in some browsers, not "No Answer").
   */
  capitalizeFallback?: boolean
}

/**
 * Known raw Twilio dial statuses. `no-answer`/`busy`/`failed` are all "the
 * transfer did not connect" — the same red treatment an ended-reason-derived
 * failure gets elsewhere in this component, so they read as one state rather
 * than three different grays. `canceled` (the transferred leg was hung up
 * before it connected) stays neutral.
 */
const TWILIO_STATUS_BADGES: Record<string, BadgeConfig> = {
  'no-answer': { text: 'No Answer', bg: 'bg-red-50', color: 'text-red-700' },
  busy: { text: 'Busy', bg: 'bg-red-50', color: 'text-red-700' },
  failed: { text: 'Failed', bg: 'bg-red-50', color: 'text-red-700' },
  canceled: { text: 'Canceled', bg: 'bg-gray-50', color: 'text-gray-700' },
}

/** Maps a transfer's state to badge copy + colors. */
function getStatusBadgeConfig(transfer: TransferDisplayInfo): BadgeConfig {
  if (transfer.kind === 'failed') {
    return { text: 'Failed', bg: 'bg-red-50', color: 'text-red-700' }
  }
  if (transfer.kind === 'forwarded') {
    // Transfer happened but no outcome was captured.
    return { text: 'Transferred', bg: 'bg-blue-50', color: 'text-blue-700' }
  }
  if (transfer.kind === 'attempted') {
    // A transferCall request went out but the call ended some other way —
    // neutral on purpose, since nothing about the outcome can be claimed.
    return { text: 'Attempted', bg: 'bg-gray-50', color: 'text-gray-700' }
  }
  if (transfer.reachedVoicemail) {
    // Checked before `connected` — voicemail is never `connected` (see
    // deriveTransferInfo), but it deserves its own visible state instead of
    // falling through to a raw "Completed" badge that reads as a false
    // positive: gray-neutral coloring plus display text that still asserts
    // success in words.
    return { text: 'Voicemail', bg: 'bg-amber-50', color: 'text-amber-700' }
  }
  if (transfer.connected) {
    return { text: 'Connected', bg: 'bg-green-50', color: 'text-green-700' }
  }
  const known = transfer.status
    ? TWILIO_STATUS_BADGES[transfer.status.toLowerCase()]
    : undefined
  if (known) return known
  return {
    text: transfer.status ?? 'Unknown',
    bg: 'bg-gray-50',
    color: 'text-gray-700',
    capitalizeFallback: true,
  }
}

function TransferRow({
  label,
  value,
}: {
  label: string
  value: React.ReactNode
}) {
  return (
    <div className="flex justify-between gap-2 py-1.5 text-sm">
      <span className="text-muted-foreground shrink-0">{label}</span>
      <span className="min-w-0 text-right">{value}</span>
    </div>
  )
}

/**
 * Body of the sheet's Transfer section. Rendered only when
 * `deriveTransferInfo` finds transfer information on the call.
 */
export function CallTransferSection({
  transfer,
}: {
  transfer: TransferDisplayInfo
}) {
  const badge = getStatusBadgeConfig(transfer)
  const hasDestination = Boolean(
    transfer.destinationName || transfer.destinationNumber
  )
  return (
    <div className="divide-y rounded-md border px-3">
      {hasDestination && (
        <TransferRow
          label="Destination"
          value={
            // Number-primary / name-secondary hierarchy matches
            // DestinationChip in the duties feature, which renders the same
            // handoff_config data — `description` is model-facing copy, not
            // curated for end users, so it stays secondary and gets a
            // `title` for the full text since it can truncate.
            <span className="flex min-w-0 flex-col items-end gap-0.5">
              <span className="font-mono text-sm font-medium">
                {formatPhone(transfer.destinationNumber)}
              </span>
              {transfer.destinationName && (
                <span
                  className="text-muted-foreground max-w-full truncate text-xs"
                  title={transfer.destinationName}>
                  {transfer.destinationName}
                </span>
              )}
            </span>
          }
        />
      )}
      <TransferRow
        label="Status"
        value={
          <Badge
            className={cn(
              'rounded-md border-transparent',
              badge.capitalizeFallback && 'capitalize',
              badge.bg,
              badge.color
            )}>
            {badge.text}
          </Badge>
        }
      />
      {transfer.duration !== null && (
        <TransferRow
          label="Duration"
          value={
            <span className="font-mono">
              {formatDuration(transfer.duration)}
            </span>
          }
        />
      )}
    </div>
  )
}
